import type {
  CmmdProject,
  CommandId,
  EnvironmentApi,
  EnvironmentId,
  ProjectId,
  ThreadEnvMode,
} from "@cmmd-forge/contracts";
import type { Project } from "../types";
import { buildPrimaryFetchInit } from "../environments/primary/forgeSessionClient";
import { registerProjectAndOpenThread } from "./addProject";
import { ForgeAuthExpiredError } from "./authExpiredRedirect";
import { classifyEnvironmentId } from "../environments/environmentIdClassification";

type FetchLike = typeof fetch;

export interface AddProjectBaseInput {
  readonly api: EnvironmentApi;
  readonly environmentId: EnvironmentId;
  readonly projects: readonly Project[];
  readonly defaultThreadEnvMode: ThreadEnvMode;
  readonly handleNewThread: (
    projectRef: { environmentId: EnvironmentId; projectId: ProjectId },
    options?: { envMode?: ThreadEnvMode; openBrowserPreview?: boolean },
  ) => Promise<void>;
  readonly focusMostRecentThreadForProject?: (projectRef: {
    environmentId: EnvironmentId;
    projectId: ProjectId;
  }) => void | Promise<void>;
  readonly openThreadAfterRegister?: boolean;
  readonly fetchImpl?: FetchLike;
  readonly linkFetchImpl?: FetchLike;
  readonly resolveHttpUrl: (path: string, searchParams?: Record<string, string>) => string;
  readonly resolveLinkHttpUrl?: (path: string, searchParams?: Record<string, string>) => string;
  readonly onTransientProjectCreateRetry?: (input: {
    readonly attempt: number;
    readonly maxAttempts: number;
    readonly error: Error;
  }) => void;
}

export interface AddProjectResult {
  readonly projectId: ProjectId;
  readonly existing: boolean;
}

export interface GitHubCloneJobSnapshot {
  readonly id: string;
  readonly repo_full_name: string;
  readonly status: "queued" | "running" | "ready" | "failed";
  readonly phase: string;
  readonly detail: string;
  readonly percent: number | null;
  readonly current: number | null;
  readonly total: number | null;
  readonly local_path: string | null;
  readonly existing: boolean;
  readonly error: string | null;
  readonly updated_at: string;
}

function toSafeAddProjectError(
  response: Response,
  backendMessage: string | undefined,
  backendCode: string | undefined,
  userActionRequired: string | undefined,
  fallbackMessage: string,
  environmentId: EnvironmentId,
): Error {
  if (backendCode === "GITHUB_AUTH_REQUIRED" || userActionRequired === "connect_github") {
    return new Error("Connect GitHub in Settings, then try importing the repository again.");
  }

  if (classifyEnvironmentId(environmentId).isCloud) {
    if (response.status === 401 || backendCode === "CMMD_REAUTH_REQUIRED") {
      return new Error(
        "Forge Cloud Environment authorization expired while importing the project. Reconnect the Environment and try again.",
      );
    }
    if (backendMessage?.includes("Authentication required")) {
      return new Error(
        "Forge Cloud Environment rejected the project import request. Reconnect the Environment and try again.",
      );
    }
  }

  if (
    response.status === 401 ||
    backendCode === "CMMD_REAUTH_REQUIRED" ||
    backendMessage?.includes("Authentication required")
  ) {
    return new ForgeAuthExpiredError();
  }
  if (response.status === 403 || backendMessage === "Forbidden.") {
    return new Error("You do not have permission to import projects in Forge.");
  }
  return new Error(backendMessage ?? `${fallbackMessage} (HTTP ${response.status})`);
}

async function readLocalPath(
  response: Response,
  fallbackMessage: string,
  environmentId: EnvironmentId,
): Promise<string> {
  const data = (await response.json().catch(() => ({}))) as {
    local_path?: string;
    error?: string;
    code?: string;
    userActionRequired?: string;
  };
  if (typeof data.local_path === "string" && data.local_path.trim().length > 0) {
    return data.local_path;
  }
  if (!response.ok) {
    throw toSafeAddProjectError(
      response,
      data.error,
      data.code,
      data.userActionRequired,
      fallbackMessage,
      environmentId,
    );
  }
  throw new Error(data.error ?? fallbackMessage);
}

async function register(input: AddProjectBaseInput, cwd: string): Promise<AddProjectResult> {
  return registerProjectAndOpenThread({
    api: input.api,
    environmentId: input.environmentId,
    projects: input.projects,
    cwd,
    defaultThreadEnvMode: input.defaultThreadEnvMode,
    handleNewThread: input.handleNewThread,
    ...(input.focusMostRecentThreadForProject
      ? { focusMostRecentThreadForProject: input.focusMostRecentThreadForProject }
      : {}),
    ...(input.openThreadAfterRegister !== undefined
      ? { openThread: input.openThreadAfterRegister }
      : {}),
  });
}

export async function registerClonedProjectPath(
  input: AddProjectBaseInput,
  cwd: string,
): Promise<AddProjectResult> {
  return register(input, cwd);
}

async function deleteProject(input: AddProjectBaseInput, projectId: ProjectId): Promise<void> {
  await input.api.orchestration.dispatchCommand({
    type: "project.delete",
    commandId: crypto.randomUUID() as CommandId,
    projectId,
  });
}

const CMMD_RUNTIME_PROJECT_CREATE_MAX_ATTEMPTS = 10;
const CMMD_RUNTIME_PROJECT_CREATE_RETRY_DELAYS_MS = [
  1_000, 2_000, 4_000, 8_000, 12_000, 16_000, 20_000, 20_000, 20_000,
] as const;

function isTransientProjectCreateError(error: unknown): error is Error {
  if (!(error instanceof Error)) return false;
  return (
    error.message === "Failed to fetch" ||
    error.message.includes("NetworkError") ||
    error.message.includes("Load failed")
  );
}

function projectCreateRetryDelayMs(attemptIndex: number): number {
  if (import.meta.env.MODE === "test") return 0;
  return (
    CMMD_RUNTIME_PROJECT_CREATE_RETRY_DELAYS_MS[attemptIndex] ??
    CMMD_RUNTIME_PROJECT_CREATE_RETRY_DELAYS_MS[
      CMMD_RUNTIME_PROJECT_CREATE_RETRY_DELAYS_MS.length - 1
    ]!
  );
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => globalThis.setTimeout(resolve, ms));
}

function buildTargetFetchInit(input: AddProjectBaseInput, init: RequestInit): RequestInit {
  return input.fetchImpl ? init : buildPrimaryFetchInit(init);
}

async function createProjectDirectory(input: AddProjectBaseInput, name: string): Promise<Response> {
  const request = () =>
    (input.fetchImpl ?? fetch)(
      input.resolveHttpUrl("/api/cloud/projects"),
      buildTargetFetchInit(input, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim() }),
      }),
    );

  // Open question, deliberately left open: broaden this cap to every cloud shape,
  // like the rest of this migration, or keep it runtime-only? A future owner's call, not resolved here.
  if (classifyEnvironmentId(input.environmentId).legacyRuntimeId === null) {
    return request();
  }

  let lastError: Error | null = null;
  for (
    let attemptIndex = 0;
    attemptIndex < CMMD_RUNTIME_PROJECT_CREATE_MAX_ATTEMPTS;
    attemptIndex += 1
  ) {
    try {
      return await request();
    } catch (error) {
      if (!isTransientProjectCreateError(error)) {
        throw error;
      }
      lastError = error;
      if (attemptIndex >= CMMD_RUNTIME_PROJECT_CREATE_MAX_ATTEMPTS - 1) {
        break;
      }
      input.onTransientProjectCreateRetry?.({
        attempt: attemptIndex + 1,
        maxAttempts: CMMD_RUNTIME_PROJECT_CREATE_MAX_ATTEMPTS,
        error,
      });
      await sleep(projectCreateRetryDelayMs(attemptIndex));
    }
  }

  throw new Error(
    lastError
      ? `Forge Cloud runtime did not become reachable while creating the project. Last error: ${lastError.message}`
      : "Forge Cloud runtime did not become reachable while creating the project.",
  );
}

export async function createEmptyProjectProject(
  input: AddProjectBaseInput & {
    readonly name: string;
    readonly cmmdProject?: CmmdProject | null;
    readonly campaignName?: string | null;
  },
): Promise<AddProjectResult> {
  const response = await createProjectDirectory(input, input.name);
  const cwd = await readLocalPath(response, "Failed to create project", input.environmentId);
  const result = await register(input, cwd);
  if (input.cmmdProject) {
    try {
      await linkForgeProjectToCmmdProject({
        ...input,
        cwd,
        projectId: result.projectId,
        project: input.cmmdProject,
        ...(input.campaignName !== undefined ? { campaignName: input.campaignName } : {}),
      });
    } catch (error) {
      if (!result.existing) {
        await deleteProject(input, result.projectId).catch(() => undefined);
      }
      throw error;
    }
  }
  return result;
}

function parseFiniteId(value: string | null | undefined): number | null {
  if (!value) return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

export function parseRequiredCmmdProjectIdentity(
  project: CmmdProject,
  workspaceIdFallback?: number | null,
): {
  readonly cmmdProjectId: number;
  readonly cmmdCampaignId: number | null;
  readonly cmmdWorkspaceId: number;
} {
  const cmmdProjectId = parseFiniteId(project.id);
  const cmmdCampaignId = parseFiniteId(project.campaignId);
  // Cross-workspace rows arrive with no workspaceId; fall back to the caller's resolved one, or import fails at the link step.
  const cmmdWorkspaceId = parseFiniteId(project.workspaceId) ?? workspaceIdFallback ?? null;
  if (!cmmdProjectId || !cmmdWorkspaceId) {
    throw new Error("CMMD project identity is incomplete. Refresh CMMD and try again.");
  }
  return { cmmdProjectId, cmmdCampaignId, cmmdWorkspaceId };
}

async function readProjectMutationError(
  response: Response,
  fallbackMessage: string,
  environmentId: EnvironmentId,
): Promise<Error> {
  const data = (await response.json().catch(() => ({}))) as {
    error?: string;
    code?: string;
    userActionRequired?: string;
  };
  return toSafeAddProjectError(
    response,
    data.error,
    data.code,
    data.userActionRequired,
    fallbackMessage,
    environmentId,
  );
}

export async function linkForgeProjectToCmmdProject(
  input: AddProjectBaseInput & {
    readonly cwd: string;
    readonly projectId: ProjectId;
    readonly project: CmmdProject;
    readonly campaignName?: string | null;
    readonly githubRepoFullName?: string | null;
    readonly source?: "linked_existing" | "created_from_cmmd" | "created_from_forge";
    readonly cmmdOrganizationId?: number;
  },
): Promise<void> {
  const { cmmdProjectId, cmmdCampaignId, cmmdWorkspaceId } = parseRequiredCmmdProjectIdentity(
    input.project,
    input.cmmdOrganizationId ?? null,
  );
  await linkForgeProjectIdentityToCmmdProject({
    environmentId: input.environmentId,
    projectId: input.projectId,
    cwd: input.cwd,
    cmmdProjectId,
    cmmdOrganizationId: cmmdWorkspaceId,
    cmmdProjectName: input.project.title,
    cmmdWorkspaceName: input.project.workspaceName ?? "Projects",
    cmmdCampaignId,
    cmmdCampaignName: input.campaignName ?? null,
    githubRepoFullName: input.githubRepoFullName ?? null,
    source: input.source ?? "created_from_cmmd",
    fetchImpl: input.linkFetchImpl ?? input.fetchImpl ?? fetch,
    resolveHttpUrl: input.resolveLinkHttpUrl ?? input.resolveHttpUrl,
    applyPrimaryAuth: input.linkFetchImpl === undefined && input.fetchImpl === undefined,
  });
}

export async function linkForgeProjectIdentityToCmmdProject(input: {
  readonly environmentId: EnvironmentId;
  readonly projectId: ProjectId;
  readonly cwd: string;
  readonly cmmdOrganizationId: number;
  readonly cmmdProjectId: number;
  readonly cmmdProjectName: string;
  readonly cmmdWorkspaceName: string;
  readonly cmmdCampaignId?: number | null;
  readonly cmmdCampaignName?: string | null;
  readonly githubRepoFullName?: string | null;
  readonly source?: "linked_existing" | "created_from_cmmd" | "created_from_forge";
  readonly fetchImpl?: FetchLike;
  readonly resolveHttpUrl: (path: string) => string;
  readonly applyPrimaryAuth?: boolean;
}): Promise<void> {
  const campaignName = input.cmmdCampaignName ?? null;
  const linkBody = {
    environmentId: input.environmentId,
    projectId: input.projectId,
    cwd: input.cwd,
    cmmdOrganizationId: input.cmmdOrganizationId,
    cmmdProjectId: input.cmmdProjectId,
    cmmdCampaignId: input.cmmdCampaignId ?? null,
    cmmdWorkspaceId: input.cmmdOrganizationId,
    cmmdWorkspaceName: input.cmmdWorkspaceName,
    workspaceName: input.cmmdWorkspaceName,
    cmmdCampaignName: campaignName,
    ...(campaignName ? { campaignName } : {}),
    cmmdProjectName: input.cmmdProjectName,
    projectName: input.cmmdProjectName,
    githubRepoFullName: input.githubRepoFullName ?? null,
    source: input.source ?? "created_from_cmmd",
    linkedAt: new Date().toISOString(),
    lastVerifiedAt: null,
  };
  const linkRequestInit: RequestInit = {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(linkBody),
  };
  const response = await (input.fetchImpl ?? fetch)(
    input.resolveHttpUrl("/.well-known/forge/project-link"),
    input.applyPrimaryAuth ? buildPrimaryFetchInit(linkRequestInit) : linkRequestInit,
  );
  if (!response.ok) {
    throw await readProjectMutationError(
      response,
      "Failed to link CMMD project",
      input.environmentId,
    );
  }
}

export async function createCmmdProjectProject(
  input: AddProjectBaseInput & {
    readonly project: CmmdProject;
    readonly campaignName?: string | null;
  },
): Promise<AddProjectResult> {
  parseRequiredCmmdProjectIdentity(input.project);
  const response = await (input.fetchImpl ?? fetch)(
    input.resolveHttpUrl("/api/cloud/projects"),
    buildTargetFetchInit(input, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name: input.project.title.trim() }),
    }),
  );
  const cwd = await readLocalPath(
    response,
    "Failed to start from CMMD project",
    input.environmentId,
  );
  const result = await register(input, cwd);

  try {
    await linkForgeProjectToCmmdProject({
      ...input,
      cwd,
      projectId: result.projectId,
    });
  } catch (error) {
    if (!result.existing) {
      await deleteProject(input, result.projectId).catch(() => undefined);
    }
    throw error;
  }
  return result;
}

export async function cloneGitHubRepositoryProject(
  input: AddProjectBaseInput & { readonly repoFullName: string },
): Promise<AddProjectResult> {
  const response = await (input.fetchImpl ?? fetch)(
    input.resolveHttpUrl("/.well-known/forge/github/clone"),
    buildTargetFetchInit(input, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ repo_full_name: input.repoFullName }),
    }),
  );
  return register(
    input,
    await readLocalPath(response, "Failed to clone GitHub repository", input.environmentId),
  );
}

function readCloneJob(
  response: Response,
  fallbackMessage: string,
  environmentId: EnvironmentId,
): Promise<GitHubCloneJobSnapshot> {
  return response
    .json()
    .catch(() => ({}))
    .then(
      (data: {
        job?: GitHubCloneJobSnapshot;
        error?: string;
        code?: string;
        userActionRequired?: string;
      }) => {
        if (!response.ok) {
          throw toSafeAddProjectError(
            response,
            data.error,
            data.code,
            data.userActionRequired,
            fallbackMessage,
            environmentId,
          );
        }
        if (!data.job) throw new Error(data.error ?? fallbackMessage);
        return data.job;
      },
    );
}

export async function startGitHubRepositoryCloneJob(
  input: AddProjectBaseInput & { readonly repoFullName: string },
): Promise<GitHubCloneJobSnapshot> {
  const response = await (input.fetchImpl ?? fetch)(
    input.resolveHttpUrl("/.well-known/forge/github/clone-jobs"),
    buildTargetFetchInit(input, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ repo_full_name: input.repoFullName }),
    }),
  );
  return readCloneJob(response, "Failed to start GitHub clone", input.environmentId);
}

export async function readGitHubRepositoryCloneJob(
  input: AddProjectBaseInput & { readonly jobId: string },
): Promise<GitHubCloneJobSnapshot> {
  const response = await (input.fetchImpl ?? fetch)(
    input.resolveHttpUrl("/.well-known/forge/github/clone-job", { job_id: input.jobId }),
    buildTargetFetchInit(input, {
      method: "GET",
      credentials: "include",
    }),
  );
  return readCloneJob(response, "Failed to read GitHub clone progress", input.environmentId);
}

export async function addDesktopFolderProject(
  input: AddProjectBaseInput & {
    readonly localApi:
      | {
          readonly dialogs: {
            readonly pickFolder: () => Promise<string | null>;
          };
        }
      | null
      | undefined;
  },
): Promise<AddProjectResult | { readonly cancelled: true }> {
  if (!input.localApi) return { cancelled: true };
  const cwd = await input.localApi.dialogs.pickFolder();
  if (!cwd) return { cancelled: true };
  return register(input, cwd);
}
